import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";
import type { FormField } from "../../components/AdminDataModal";
import { useMessageModal } from "../../contexts/MessageModalContext";
import { useFormat } from "../../../context/FormatContext";

export default function AdminPolicies() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const navigate = useNavigate();
  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const fetchPolicies = async () => {
    try {
      const res = await fetch(`${BASE_URL}/admin/settings/policies`, {
        credentials: "include",
      });

      const result = await res.json();

      if (result.success) {
        setData(result.data || []);
      }
    } catch (error) {
      console.error("Fetch Policies Error:", error);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Policy",
      message: `Are you sure you want to delete "${row.title}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(
            `${BASE_URL}/admin/settings/policies/${row.id}`,
            {
              method: "DELETE",
              credentials: "include",
            },
          );

          const result = await res.json();

          if (result.success) {
            fetchPolicies();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Policy deleted successfully.",
              confirmText: "OK",
            });
          } else {
            showModal({
              type: "error",
              title: "Error",
              message: result.message || "Failed to delete policy.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Policy Error:", error);

          showModal({
            type: "error",
            title: "Error",
            message: "Something went wrong while deleting the policy.",
            confirmText: "OK",
          });
        }
      },
    });
  };

  const policyFields: FormField[] = [
    { key: "id", label: "ID", colSpan: 2 },
    { key: "title", label: "Policy Title", colSpan: 2 },
    { key: "policy_key", label: "Policy Key", colSpan: 2 },
    {
      key: "content",
      label: "Policy Content",
      type: "textarea",
      colSpan: 2,
    },
    { key: "target_page", label: "Target Page", colSpan: 1 },
    { key: "placement", label: "Placement", colSpan: 1 },
    { key: "status", label: "Status", colSpan: 2 },
  ];

  const columns = [
    { key: "title", label: "Policy Title" },
    { key: "created_at", label: "Created At" },
    { key: "updated_at", label: "Updated At" },
    { key: "status", label: "Status" },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Policies"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => navigate("/admin/policies/create")}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => navigate(`/admin/policies/edit/${row.id}`)}
        onSoftDelete={handleDelete}
        enableExport={true}
      >
        {(filteredData) => (
          <div className="algorithm-grid">
            {filteredData.map((item: any) => (
              <div
                key={item.id}
                className={`algorithm-card ${
                  selectedRow?.id === item.id ? "selected" : ""
                }`}
                onClick={() => setSelectedRow(item)}
              >
                <h3>{item.title}</h3>

                <p>
                  <strong>Created At : </strong>{" "}
                  {formatDateToDMY(item.created_at) || "-"}
                </p>

                <p>
                  <strong>Updated At : </strong>{" "}
                  {formatDateToDMY(item.updated_at) || "-"}
                </p>

                {item.target_page && (
                  <p>
                    <strong>Page:</strong> {item.target_page}
                  </p>
                )}

                {item.placement && (
                  <p>
                    <strong>Placement:</strong> {item.placement}
                  </p>
                )}

                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={
                      item.status === "active"
                        ? "badge-success"
                        : "badge-danger"
                    }
                  >
                    {item.status}
                  </span>
                </p>
              </div>
            ))}
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Policy"
        fields={policyFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
